import axios from 'axios';
import { ensureCsrfToken } from './csrf';
import type { DeclaredWithdrawal, PublicWithdrawal } from '../types';

// Page publique « Renoncer au contrat ici » (vente à distance, T17), ouverte par le lien de l'e-mail de fin de
// signature, sans compte. Client dédié, comme la page de signature (ContractSigningPage) : aucune session de
// l'espace élève ; jeton toujours dans le corps (jamais dans une adresse), en-tête CSRF.

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  withCredentials: true,
});

/** Jeton du lien (32 octets en base64url) : tout autre texte est un lien invalide, sans appel au serveur. */
export const WITHDRAWAL_TOKEN = /^[A-Za-z0-9_-]{43}$/;

async function post<T>(path: 'view' | 'confirm', data: unknown): Promise<T> {
  const csrf = await ensureCsrfToken();
  const response = await api.post<T>(`/v1/sale-withdrawals/link/${path}`, data, {
    headers: { 'x-csrf-token': csrf ?? '' },
  });
  return response.data;
}

/** École, document et dates du lien ; nom et adresse proposés seulement pendant le délai ouvert. 404 : lien invalide. */
export function viewLink(token: string): Promise<PublicWithdrawal> {
  return post<PublicWithdrawal>('view', { token });
}

/** Rétractation par le lien : rend la vue avec l'accusé ENREGISTRÉ (`ack`). 409 : délai passé ; 404 : lien invalide. */
export function confirmLink(token: string, declared: DeclaredWithdrawal): Promise<PublicWithdrawal> {
  return post<PublicWithdrawal>('confirm', {
    token,
    name: declared.name,
    contract: declared.contract,
    email: declared.email,
  });
}

/** Code HTTP d'un appel refusé (0 sans réponse du serveur). */
export function statusOf(error: unknown): number {
  return axios.isAxiosError(error) ? (error.response?.status ?? 0) : 0;
}

/** Message d'un appel refusé : celui du serveur (texte, ou liste des refus de validation), sinon un message général. */
export function withdrawalErrorText(error: unknown): string {
  const fallback = 'Opération indisponible. Réessayez.';
  if (axios.isAxiosError(error)) {
    const message: unknown = error.response?.data?.message;
    if (Array.isArray(message) && message.length) return message.map(String).join(' ');
    return typeof message === 'string' && message ? message : fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}
